import { isOnline } from "./network";
import {
  OUTBOX_STORE,
  deleteRecord,
  getAllRecordsByIndex,
  getMeta,
  putRecord,
  setMeta,
  updateRecord,
} from "./storage";
import { createUuid, isBrowser } from "./types";

/**
 * Cola de salida (*outbox*).
 *
 * **Una sola cola para todos los módulos** (§10.1). Registros de asistencia, respuestas
 * de formulario y justificaciones comparten el mismo mecanismo: si cada módulo
 * inventara el suyo, cada uno tendría sus propios bugs de reintento.
 *
 * Las tres reglas que la definen:
 *
 * 1. **Nada se descarta solo.** Un elemento que el servidor rechaza no se borra: pasa
 *    a `blocked` y espera intervención explícita. Una marca perdida es una hora de
 *    trabajo no pagada (§8.3.5).
 * 2. **Idempotencia por UUID.** El identificador se genera en el cliente y viaja con
 *    el envío, así que reintentar nunca duplica. Sin esto, un reintento tras un
 *    timeout ambiguo crea registros fantasma.
 * 3. **El estado siempre es visible.** Cuántos pendientes y cuándo fue la última
 *    sincronización, en todo momento — el empleado tiene que poder demostrar que
 *    marcó aunque el dato aún no haya llegado al servidor.
 */

export type OutboxItemStatus =
  /** En cola, esperando su turno o su próximo reintento. */
  | "pending"
  /**
   * El servidor lo rechazó de forma definitiva. **Deja de ser un pendiente del
   * empleado**: no cuenta en el indicador ni se sigue reintentando. Pasa a ser un
   * caso que el administrador revisa y puede corregir (§13.1 #21).
   */
  | "rejected";

export type OutboxItem<TPayload = unknown> = {
  /** Llave de idempotencia. Viaja al servidor; reintentar con el mismo id no duplica. */
  id: string;
  /** Qué es esto: `"attendance.punch"`, `"activity.form"`… Determina quién sabe enviarlo. */
  kind: string;
  payload: TPayload;
  /** Texto corto para la pantalla de pendientes ("Entrada, 7:02 a. m."). */
  label: string | null;
  status: OutboxItemStatus;
  /** Reloj del teléfono al encolar. El servidor registra el suyo (§8.3.2). */
  created_at: string;
  attempts: number;
  last_attempt_at: string | null;
  next_attempt_at: string;
  /** Naturaleza del último fallo. Decide qué se le dice al empleado. */
  last_failure_kind: OutboxFailureKind | null;
  /** Detalle técnico del último fallo, para diagnóstico y para el motivo del rechazo. */
  last_error: string | null;
  /**
   * Cuándo se logró reportar el rechazo al servidor para que lo revise un
   * administrador. Mientras sea `null`, el reporte sigue intentándose.
   */
  escalated_at: string | null;
};

/**
 * Las tres formas de fallar son **tres situaciones distintas para el empleado**, no
 * grados de la misma. Colapsarlas en un booleano "reintentable" haría que la app
 * dijera lo mismo ante "estás en un sótano sin señal" y ante "te dieron de baja
 * ayer", que es justo lo que no debe pasar.
 */
export type OutboxFailureKind =
  /**
   * No hubo respuesta: sin red, DNS caído, tiempo agotado. **No sabemos si el envío
   * llegó** — de ahí que la idempotencia por UUID no sea opcional. Se reintenta.
   */
  | "no_response"
  /**
   * El servidor respondió, pero con un fallo suyo (5xx). Entendió la petición y no
   * pudo atenderla. Se reintenta, pero no es problema del teléfono del empleado y no
   * se le debe comunicar como si lo fuera.
   */
  | "server_error"
  /**
   * El servidor respondió que no: validación, permiso, bandera apagada, turno ya
   * cerrado. Reintentar daría exactamente lo mismo. Requiere que alguien decida.
   */
  | "rejected";

export type OutboxSendResult =
  | { ok: true }
  | { ok: false; kind: OutboxFailureKind; message: string };

export type OutboxSender<TPayload = unknown> = (
  item: OutboxItem<TPayload>,
) => Promise<OutboxSendResult>;

export type OutboxModule<TPayload = unknown> = {
  send: OutboxSender<TPayload>;
  /**
   * Reporta al servidor un elemento rechazado, para que un administrador lo revise y
   * pueda corregirlo (§13.1 #21).
   *
   * **Es un envío distinto del original**, contra un destino que acepta siempre: el
   * original fue rechazado por una regla de negocio —turno ya cerrado, persona dada
   * de baja— y reintentarlo daría lo mismo. Lo que se reporta no es la marca, es el
   * hecho de que hubo una marca que no pudo registrarse.
   *
   * Si el módulo no la implementa, el elemento se queda en el teléfono en estado
   * `rejected`: no se pierde, pero nadie se entera. Todo módulo que encole algo con
   * valor laboral debería implementarla.
   */
  escalate?: (item: OutboxItem<TPayload>) => Promise<{ ok: boolean }>;
};

const LAST_SUCCESS_META_KEY = "outbox.last_success_at";

/** 5 s, 10 s, 20 s, 40 s… con tope de 10 min. */
const BACKOFF_BASE_MS = 5_000;
const BACKOFF_MAX_MS = 10 * 60_000;
const AUTO_FLUSH_INTERVAL_MS = 60_000;

const modules = new Map<string, OutboxModule>();
const listeners = new Set<() => void>();

let flushInFlight: Promise<OutboxFlushSummary> | null = null;

/**
 * Cada módulo registra cómo se envía lo suyo, normalmente al cargar su pantalla.
 * La cola no sabe nada de asistencia ni de formularios: solo reparte.
 */
export function registerOutboxModule<TPayload>(
  kind: string,
  outboxModule: OutboxModule<TPayload>,
): void {
  modules.set(kind, outboxModule as OutboxModule);
}

/** Avisa a la interfaz. Se llama después de cada cambio en la cola. */
function notify(): void {
  listeners.forEach((listener) => {
    try {
      listener();
    } catch {
      // Un componente roto no puede tumbar la sincronización.
    }
  });
}

export function subscribeToOutbox(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Espera creciente con dispersión aleatoria: si el servidor vuelve tras una caída,
 * los teléfonos no lo golpean todos en el mismo instante.
 */
function nextAttemptAt(attempts: number): string {
  const base = Math.min(BACKOFF_BASE_MS * 2 ** attempts, BACKOFF_MAX_MS);
  const jitter = base * 0.2 * (Math.random() * 2 - 1);
  return new Date(Date.now() + base + jitter).toISOString();
}

export async function enqueue<TPayload>(
  kind: string,
  payload: TPayload,
  options: { id?: string; label?: string } = {},
): Promise<OutboxItem<TPayload>> {
  const now = new Date().toISOString();

  const item: OutboxItem<TPayload> = {
    id: options.id ?? createUuid(),
    kind,
    payload,
    label: options.label ?? null,
    status: "pending",
    created_at: now,
    attempts: 0,
    last_attempt_at: null,
    // Elegible de inmediato: el primer intento es ahora mismo.
    next_attempt_at: now,
    last_failure_kind: null,
    last_error: null,
    escalated_at: null,
  };

  await putRecord(OUTBOX_STORE, item);
  notify();
  return item;
}

const readAll = (): Promise<OutboxItem[]> =>
  getAllRecordsByIndex<OutboxItem>(OUTBOX_STORE, "created_at");

export type OutboxFlushSummary = {
  sent: number;
  retrying: number;
  rejected: number;
  /** Rechazos que se lograron reportar al administrador y salieron del teléfono. */
  escalated: number;
  /** Sin módulo registrado todavía: su código aún no se ha cargado. */
  skipped: number;
};

/**
 * Procesa la cola. **En serie y en orden de creación**, deliberadamente: una salida
 * no puede llegar al servidor antes que su entrada, y enviar en paralelo desde una
 * conexión de campo suele empeorar el rendimiento en vez de mejorarlo.
 *
 * Llamadas concurrentes comparten el mismo trabajo — dos "sincronizar" seguidos no
 * envían nada dos veces.
 *
 * `force` ignora la espera de reintento (`next_attempt_at`) de los pendientes. Es
 * solo para el "enviar ahora" que pide el empleado: los disparos automáticos
 * respetan el backoff para no golpear a un servidor caído. Una pasada forzada no
 * se funde con una automática en curso, porque esa habría saltado los pendientes
 * en espera; se encadena a continuación.
 */
export function flushOutbox(
  options: { force?: boolean } = {},
): Promise<OutboxFlushSummary> {
  if (flushInFlight) {
    return options.force
      ? flushInFlight.then(() => flushOutbox(options))
      : flushInFlight;
  }

  flushInFlight = runFlush(options.force ?? false).finally(() => {
    flushInFlight = null;
  });

  return flushInFlight;
}

async function runFlush(force: boolean): Promise<OutboxFlushSummary> {
  const summary: OutboxFlushSummary = {
    sent: 0,
    retrying: 0,
    rejected: 0,
    escalated: 0,
    skipped: 0,
  };

  if (!isBrowser()) return summary;

  let items: OutboxItem[];
  try {
    items = await readAll();
  } catch {
    // Sin almacenamiento no hay cola que procesar; el error ya se reportó al encolar.
    return summary;
  }

  const now = Date.now();
  const due = items.filter(
    (item) =>
      item.status === "pending" &&
      (force || new Date(item.next_attempt_at).getTime() <= now),
  );

  // Rechazos que todavía no se han podido poner en manos del administrador.
  const toEscalate = items.filter(
    (item) => item.status === "rejected" && !item.escalated_at,
  );

  if (due.length === 0 && toEscalate.length === 0) return summary;

  let changed = false;

  for (const item of due) {
    const outboxModule = modules.get(item.kind);
    if (!outboxModule) {
      // No cuenta como intento: el elemento está bien, es el código lo que no está
      // cargado. Se enviará cuando su módulo se monte.
      summary.skipped += 1;
      continue;
    }

    let result: OutboxSendResult;
    try {
      result = await outboxModule.send(item);
    } catch (error) {
      // Una excepción del remitente es, casi siempre, la petición que nunca salió.
      result = {
        ok: false,
        kind: "no_response",
        message: error instanceof Error ? error.message : "Fallo de envío.",
      };
    }

    changed = true;

    if (result.ok) {
      await deleteRecord(OUTBOX_STORE, item.id);
      await setMeta(LAST_SUCCESS_META_KEY, new Date().toISOString());
      summary.sent += 1;
      continue;
    }

    // A partir de aquí el envío falló. Se fija en una constante para conservar el
    // tipo estrecho dentro del callback de la transacción.
    const failure = result;
    const retryable = failure.kind !== "rejected";
    const attempts = item.attempts + 1;
    const attemptedAt = new Date().toISOString();

    await updateRecord<OutboxItem>(OUTBOX_STORE, item.id, (current) => {
      if (!current) return null;
      return {
        ...current,
        attempts,
        last_attempt_at: attemptedAt,
        last_failure_kind: failure.kind,
        last_error: failure.message,
        status: retryable ? "pending" : "rejected",
        next_attempt_at: retryable
          ? nextAttemptAt(attempts)
          : current.next_attempt_at,
      };
    });

    if (retryable) {
      summary.retrying += 1;
    } else {
      summary.rejected += 1;
      // Se reporta en el mismo recorrido: el administrador no debería enterarse un
      // minuto más tarde de lo necesario.
      toEscalate.push({ ...item, status: "rejected", escalated_at: null });
    }

    // Sin respuesta y sin red, los siguientes fallarán igual: se corta aquí y se
    // reintenta la cola entera al recuperar conexión. Un 5xx no corta — puede ser
    // de ese envío en concreto y no del servidor entero.
    if (failure.kind === "no_response" && !isOnline()) break;
  }

  for (const item of toEscalate) {
    const escalate = modules.get(item.kind)?.escalate;
    // Sin manera de reportarlo, se queda en el teléfono. No se pierde, pero tampoco
    // se le puede decir al empleado que alguien lo está viendo.
    if (!escalate) continue;

    let reported = false;
    try {
      reported = (await escalate(item)).ok;
    } catch {
      reported = false;
    }

    if (!reported) continue;

    // El servidor ya tiene el caso: el teléfono deja de ser su custodio.
    await deleteRecord(OUTBOX_STORE, item.id);
    summary.escalated += 1;
    changed = true;
  }

  if (changed) notify();
  return summary;
}

export type OutboxSnapshot = {
  /**
   * Lo que va a llegar solo. **Este es el número del indicador**: un rechazo ya no es
   * asunto del empleado y no debe engordar la cuenta de "te falta enviar" (§13.1 #21).
   */
  pending: number;
  /** Rechazados que aún no se han podido poner en manos del administrador. */
  rejected: number;
  /** Antigüedad del pendiente más viejo: la señal de que algo va mal. */
  oldest_pending_at: string | null;
  last_success_at: string | null;
  items: OutboxItem[];
};

/** Lo que la interfaz necesita para el indicador permanente de estado (§8.3.5). */
export async function getOutboxSnapshot(): Promise<OutboxSnapshot> {
  const empty: OutboxSnapshot = {
    pending: 0,
    rejected: 0,
    oldest_pending_at: null,
    last_success_at: null,
    items: [],
  };

  if (!isBrowser()) return empty;

  try {
    const [items, lastSuccess] = await Promise.all([
      readAll(),
      getMeta<string>(LAST_SUCCESS_META_KEY),
    ]);

    const pending = items.filter((item) => item.status === "pending");

    return {
      pending: pending.length,
      rejected: items.length - pending.length,
      oldest_pending_at: pending[0]?.created_at ?? null,
      last_success_at: lastSuccess ?? null,
      items,
    };
  } catch {
    return empty;
  }
}

/**
 * Qué se le dice al empleado sobre un elemento que no ha salido.
 *
 * Vive aquí, y no en cada pantalla, para que la app no acabe diciendo tres cosas
 * distintas sobre la misma situación. El tono es deliberado: los dos primeros casos
 * son tranquilizadores porque no hay nada que el empleado deba hacer, y el tercero es
 * el único que pide atención.
 */
export function outboxFailureMessage(item: OutboxItem): string {
  if (item.status === "pending" && item.attempts === 0) {
    return "En espera de enviarse.";
  }

  switch (item.last_failure_kind) {
    case "no_response":
      return "Sin conexión. Se enviará automáticamente en cuanto haya señal.";
    case "server_error":
      return "El sistema no está respondiendo. Se sigue reintentando; no hace falta que hagas nada.";
    case "rejected":
      // No se le pide nada al empleado: ya está en manos de quien puede arreglarlo.
      return item.last_error
        ? `No se pudo registrar (${item.last_error}). Lo está revisando administración.`
        : "No se pudo registrar. Lo está revisando administración.";
    default:
      return "En espera de enviarse.";
  }
}

/**
 * Devuelve un elemento rechazado a la cola. Es la salida de emergencia para cuando la
 * causa se corrigió pero el caso sigue en el teléfono por no haberse podido reportar.
 */
export async function retryOutboxItem(id: string): Promise<void> {
  await updateRecord<OutboxItem>(OUTBOX_STORE, id, (current) => {
    if (!current) return null;
    return {
      ...current,
      status: "pending",
      attempts: 0,
      last_failure_kind: null,
      last_error: null,
      next_attempt_at: new Date().toISOString(),
    };
  });
  notify();
  void flushOutbox();
}

/**
 * Único camino por el que algo sale de la cola sin haberse enviado.
 *
 * **Solo puede invocarse desde una acción explícita del usuario**, con confirmación
 * y diciendo qué se pierde. Ninguna rutina automática —ni limpieza, ni caducidad, ni
 * migración de versión— puede llamar a esto (§10.1).
 */
export async function discardOutboxItem(id: string): Promise<void> {
  await deleteRecord(OUTBOX_STORE, id);
  notify();
}

/**
 * Arranca la sincronización automática. Devuelve la función para detenerla.
 *
 * Se dispara en los tres momentos que importan: al volver la red, al reabrir la app
 * —único mecanismo disponible en iOS, donde Background Sync no existe (§8.3.5)— y de
 * forma periódica, para el caso del wifi que reporta conexión pero no tiene salida.
 */
export function startOutboxSync(
  options: { intervalMs?: number } = {},
): () => void {
  if (!isBrowser()) return () => {};

  const attempt = () => {
    if (!isOnline()) return;
    void flushOutbox();
  };

  const handleVisibility = () => {
    if (document.visibilityState === "visible") attempt();
  };

  window.addEventListener("online", attempt);
  document.addEventListener("visibilitychange", handleVisibility);
  const interval = window.setInterval(
    attempt,
    options.intervalMs ?? AUTO_FLUSH_INTERVAL_MS,
  );

  attempt();

  return () => {
    window.removeEventListener("online", attempt);
    document.removeEventListener("visibilitychange", handleVisibility);
    window.clearInterval(interval);
  };
}
